"use client"
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form"
import { Input } from "@/components/ui/input";
import Image from "next/image"
import { useState } from "react";
import { showToast } from "@/lib/toast";
import { handleApiErrors } from "@/lib/api-error-utils";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import z from "zod";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { zodResolver } from "@hookform/resolvers/zod";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { Lock } from "lucide-react";
import { EyeOff } from "lucide-react";
import { Eye } from "lucide-react";
import { Mail } from "lucide-react";

// Define schemas for each step
export const emailSchema = z.object({
  email: z.string("Invalid email address"),
});

export const tokenSchema = z.object({
  code: z.string().regex(/^\d{6}$/, "Verification code must be 6 digits"),
});

export const passwordSchema = z.object({
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string(),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export default function ForgotPassword({ openForgotPasswordDialogue, setOpenForgotPasswordDialogue }) {

  const [message, setMessage] = useState(false);
  const [action, setAction] = useState("send");
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Initialize forms for each step
  const emailForm = useForm({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "" },
  });

  const tokenForm = useForm({
    resolver: zodResolver(tokenSchema),
    defaultValues: { code: "" },
  });

  const passwordForm = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: "",
      confirmPassword: ""
    },
  });

  const handleSendToken = async (formData) => {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/auth/forgot-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );
      const data = await response.json();

      if (response.ok) {
        // emailForm.reset();
        setAction("verify")
        showToast(data?.message || "Success", "success");
      } else {
        if (response.status === 400 || response.status === 409) {
          // showToast("Invalid email address", "error");
          handleApiErrors(emailForm, data.errors);
          return;
        }
        showToast(data?.message || "An error occurred during signin", "error");
        return;
      }
      return;
    } catch (error) {
      showToast(error?.message || "An error occurred during signin", "error");
    }
  }

  const handleVerifyToken = async (formData) => {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/auth/verify-forgot-password-code`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: Number(formData.code) }),
        }
      );
      const data = await response.json();

      if (response.ok) {
        // tokenForm.reset();
        setAction('reset')
        showToast(data?.message || "Success", "success");
      } else {
        if (response.status === 400 || response.status === 409) {
          handleApiErrors(tokenForm, data.errors);
          return;
        }
        showToast(data?.message || "An error occurred during signin", "error");
        return;
      }
      return;
    } catch (error) {
      showToast(error?.message || "An error occurred during signin", "error");
    }
  }

  const handleResetPassword = async (formData) => {

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/auth/reset-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code: Number(tokenForm.getValues().code),
            password: formData.password,
            confirmPassword: formData.confirmPassword
          }),
        }
      );
      const data = await response.json();

      if (response.ok) {
        // tokenForm.reset();
        setOpenForgotPasswordDialogue(false)
        // setAction('send')
        showToast(data?.message || "Success", "success");
      } else {
        if (response.status === 400 || response.status === 409) {
          // showToast("Invalid token", "error");
          handleApiErrors(tokenForm, data.errors);
          return;
        }
        showToast(data?.message || "An error occurred during signin", "error");
        return;
      }
      return;
    } catch (error) {
      showToast(error?.message || "An error occurred during signin", "error");
    }
  }

  return (
    <>
      <Dialog open={openForgotPasswordDialogue} onOpenChange={setOpenForgotPasswordDialogue}>
        <form>
          <DialogContent className="sm:max-w-[425px]"
            onPointerDownOutside={(e) => e.preventDefault()} // Prevent closing on backdrop click
            onInteractOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle>
                Forgot Password
                {/* {tokenSended ?} */}
              </DialogTitle>
            </DialogHeader>
            <div className="pt-4">
              {(action === "send" || action === null) &&
                <Form {...emailForm}>
                  <form onSubmit={emailForm.handleSubmit(handleSendToken)} className="space-y-4">
                    <FormLabel className="text-center w-full text-gray-700">Please enter your email:</FormLabel>
                    <FormField
                      control={emailForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem className="space-y-2">
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                            <FormControl>
                              <Input
                                id="email"
                                type="text"
                                placeholder="Your Email "
                                className="pl-10 h-12 bg-gray-50 border-gray-200 text-[#776F69] rounded-xl"
                                {...field}
                              />
                            </FormControl>
                          </div>
                          <FormMessage className="text-sm text-red-600" />
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="destructive" className="rounded-full" onClick={() => setOpenForgotPasswordDialogue(false)}>
                        Cancel
                      </Button>

                      <Button type="submit" variant="default" className="rounded-full">
                        {emailForm.formState.isSubmitting ? (
                          <>
                            Sending...
                            <Loader2 className="ml-2 size-6 animate-spin" />
                          </>
                        ) : (
                          'Send Code'
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>
              }

              {action === "verify" &&
                <Form {...tokenForm}>
                  <form onSubmit={tokenForm.handleSubmit(handleVerifyToken)} className="space-y-4">

                    <FormLabel className="text-center w-full text-gray-700">Please enter the code sent to your email:</FormLabel>
                    <FormField
                      control={tokenForm.control}
                      name="code"
                      rules={{ required: 'Verification code is required' }}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <div className="flex flex-col items-center">
                              <div className="w-fit">
                                <FormLabel className="mb-2 block text-gray-700 w-full text-center">
                                  Verification Code
                                </FormLabel>
                                <InputOTP
                                  maxLength={6}
                                  pattern={REGEXP_ONLY_DIGITS}
                                  {...field}
                                >
                                  <InputOTPGroup>
                                    {[0, 1, 2, 3, 4, 5].map((index) => (
                                      <InputOTPSlot key={index} index={index} />
                                    ))}
                                  </InputOTPGroup>
                                </InputOTP>
                              </div>
                            </div>

                          </FormControl>
                          <FormMessage className={'w-full text-center'} />
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="destructive" className="rounded-full" onClick={() => setOpenForgotPasswordDialogue(false)}>
                        Cancel
                      </Button>

                      <Button type="submit" variant="default" className="rounded-full">
                        {tokenForm.formState.isSubmitting ? (
                          <>
                            Verifying...
                            <Loader2 className="ml-2 size-6 animate-spin" />
                          </>
                        ) : (
                          'Verify Code'
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>
              }

              {action === "reset" &&
                <Form {...passwordForm}>
                  <form onSubmit={passwordForm.handleSubmit(handleResetPassword)} className="space-y-4">

                    <FormLabel className="text-center w-full">Create a new password</FormLabel>
                    {/* Password */}
                    <FormField
                      control={passwordForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem className="space-y-2">
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 sm:h-5 w-4 sm:w-5 " />
                            <FormControl>
                              <Input
                                name="password"
                                type={showPassword ? "text" : "password"}
                                placeholder="Password"
                                className="pl-10 pr-10 h-11 sm:h-12 bg-gray-50 border-gray-200 rounded-xl"
                                {...field}
                              />
                            </FormControl>
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black"
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 sm:h-5 w-4 sm:w-5 cursor-pointer text-gray-500" />
                              ) : (
                                <Eye className="h-4 sm:h-5 w-4 sm:w-5 cursor-pointer text-gray-500" />
                              )}
                            </button>
                          </div>
                          <FormMessage className="text-xs sm:text-sm text-red-600" />
                        </FormItem>
                      )}
                    />

                    {/* Confirm Password */}
                    <FormField
                      control={passwordForm.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem className="space-y-2">
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 sm:h-5 w-4 sm:w-5" />
                            <FormControl>
                              <Input
                                name="confirmPassword"
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Confirm Password"
                                className="pl-10 pr-10 h-11 sm:h-12 bg-gray-50 border-gray-200 rounded-xl"
                                {...field}
                              />
                            </FormControl>
                            <button
                              type="button"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black"
                            >
                              {showConfirmPassword ? (
                                <EyeOff className="h-4 sm:h-5 w-4 sm:w-5 text-gray-500 cursor-pointer" />
                              ) : (
                                <Eye className="h-4 sm:h-5 w-4 sm:w-5  text-gray-500 cursor-pointer" />
                              )}
                            </button>
                          </div>
                          <FormMessage className="text-xs sm:text-sm text-red-600" />
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="destructive" className="rounded-full" onClick={() => setOpenForgotPasswordDialogue(false)}>
                        Cancel
                      </Button>

                      <Button type="submit" variant="default" className="rounded-full">
                        {passwordForm.formState.isSubmitting ? (
                          <>
                            Resetting...
                            <Loader2 className="ml-2 size-6 animate-spin" />
                          </>
                        ) : (
                          'Reset Password'
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>
              }
            </div>
          </DialogContent>
        </form>
      </Dialog>
    </>
  )
}